import { Check, ChevronsUpDown, Lock, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useConfigurations } from '../../api/hooks/use-configurations';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';

export function BrandSwitcher() {
    const { identifier } = useParams<{ identifier: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { data: brands } = useConfigurations();

    const currentBrand = brands?.find((b) => b.identifier === identifier);

    const filteredBrands = useMemo(() => {
        if (!brands) return [];
        if (!searchQuery.trim()) return brands;

        const query = searchQuery.toLowerCase();
        return brands.filter(
            (brand) => brand.name.toLowerCase().includes(query) || brand.identifier.toLowerCase().includes(query),
        );
    }, [brands, searchQuery]);

    const handleBrandSelect = (brandIdentifier: string) => {
        const currentPath = location.pathname;
        const brandPathPrefix = identifier ? `/brands/${identifier}` : '';
        const tabPath =
            brandPathPrefix && currentPath.startsWith(brandPathPrefix) ? currentPath.slice(brandPathPrefix.length) : '';

        void navigate(`/brands/${brandIdentifier}${tabPath}`);
        setOpen(false);
        setSearchQuery('');
    };

    if (!currentBrand) {
        return <span className="font-medium">{identifier}</span>;
    }

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    role="combobox"
                    aria-expanded={open}
                    className="h-auto justify-between gap-2 px-2 py-1 font-medium hover:bg-accent"
                >
                    <span>{currentBrand.name}</span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[400px] p-0" align="start">
                <div className="flex items-center border-b px-3">
                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <Input
                        type="text"
                        placeholder="Search brands by name or ID..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                        }}
                        className="h-11 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                </div>

                <div className="max-h-[300px] overflow-y-auto p-1">
                    {filteredBrands.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 py-6 text-center">
                            <Search className="h-8 w-8 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">No brands found</p>
                        </div>
                    ) : (
                        filteredBrands.map((brand) => (
                            <DropdownMenuItem
                                key={brand.identifier}
                                onClick={() => {
                                    handleBrandSelect(brand.identifier);
                                }}
                                className="cursor-pointer px-2 py-2"
                            >
                                <div className="flex min-w-0 flex-1 items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium">{brand.name}</div>
                                        <div className="truncate text-xs text-muted-foreground">{brand.identifier}</div>
                                    </div>
                                    {brand.locked && (
                                        <Badge variant="destructive" className="shrink-0 gap-1">
                                            <Lock className="h-3 w-3" />
                                            Locked
                                        </Badge>
                                    )}
                                </div>
                                {brand.identifier === identifier && <Check className="h-4 w-4 shrink-0" />}
                            </DropdownMenuItem>
                        ))
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
